import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslatePipe } from '@kirigami/shared/i18n';
import { KirigamiButton } from '@kirigami/shared/ui';

/**
 * Landing hero, from the owner's poster in Figma ("You know your research
 * better than any tool", Kirigami Design Tech, node 7639:5135): the copy on
 * the left on the poster's pale blue, the second half of the title in brand
 * blue, and the open book on the right (node 7636:4914, the owner's choice
 * 2026-11-03), under the copy on small screens. The logo stays in the site
 * header. Staggered intro animation and a scroll-driven exit fade where
 * supported, as before.
 * The CTA keeps emitting `primary`; the page decides what it does.
 */
@Component({
  selector: 'app-hero-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [KirigamiButton, TranslatePipe, UpperCasePipe],
  template: `
    <section id="hero" class="hero" aria-labelledby="hero-title">
      <div class="hero__layout">
        <div class="hero__inner">
          <p class="hero__eyebrow">{{ 'landing.hero.eyebrow' | t }}</p>
          <h1 id="hero-title" class="hero__title">
            <span class="hero__line hero__line--1">{{ 'landing.hero.titleLine1' | t }}</span>
            <span class="hero__line hero__line--2">{{ 'landing.hero.titleLine2' | t }}</span>
          </h1>
          <p class="hero__subtitle">
            <span class="hero__subline">{{ 'landing.hero.subtitleLine1' | t }}</span>
            <span class="hero__subline">{{ 'landing.hero.subtitleLine2' | t }}</span>
          </p>
          <div class="hero__actions">
            <kirigami-button
              [label]="'landing.hero.primaryCta' | t | uppercase"
              [ariaLabel]="'landing.hero.primaryCta' | t"
              [filled]="true"
              styleClass="hero-cta"
              (onClick)="primary.emit()"
            />
          </div>
        </div>
        <div class="hero__art" aria-hidden="true">
          <img
            class="hero__art-img"
            src="img/hero-book.webp"
            alt=""
            width="1122"
            height="1402"
            decoding="async"
          />
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      /* The topbar floats over the page, so the hero starts at y=0 and its
         pale blue runs behind the transparent header. */
      .hero {
        min-height: min(100svh, 56rem);
        display: flex;
        align-items: center;
        padding: 7.5rem 0 4rem;
        background: var(--kirigami-50);
      }
      /* The copy on the left, lined up with the header's logo (its padding);
         the book on the right, under the copy on small screens. */
      .hero__layout {
        width: 100%;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        align-items: center;
        gap: 2.5rem 3rem;
        padding: 0 1.5rem;
      }
      @media (min-width: 1024px) {
        .hero__layout {
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          padding: 0 4.0625rem;
        }
      }
      .hero__inner {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      /* The poster's scale, 43 to 102: the eyebrow and the subtitle are
         0.42 of the title. */
      .hero__eyebrow {
        margin: 0 0 1.25rem;
        color: var(--kirigami-600);
        font-size: clamp(1.125rem, 2.4vw, 2.125rem);
        font-weight: 400;
        line-height: 1.2;
        letter-spacing: -0.04em;
        animation: hero-rise 1.05s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      .hero__title {
        margin: 0;
        font-weight: 700;
        font-size: clamp(2.625rem, 6.4vw, 5.125rem);
        line-height: 1.02;
        letter-spacing: -0.025em;
        text-wrap: balance;
        color: var(--neu-950);
      }
      .hero__line {
        display: block;
        animation: hero-rise 1.05s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      .hero__line--1 {
        animation-delay: 0.12s;
      }
      .hero__line--2 {
        color: var(--kirigami-600);
        animation-delay: 0.24s;
      }
      .hero__subtitle {
        margin: 2rem 0 0;
        color: var(--neu-900);
        font-size: clamp(1.125rem, 2.4vw, 2.125rem);
        font-weight: 400;
        line-height: 1.14;
        letter-spacing: -0.04em;
        animation: hero-rise 1.05s cubic-bezier(0.16, 1, 0.3, 1) 0.41s both;
      }
      .hero__subline {
        display: block;
      }
      .hero__actions {
        margin-top: 2.5rem;
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        animation: hero-rise 1.05s cubic-bezier(0.16, 1, 0.3, 1) 0.55s both;
      }

      /* The book as Figma frames it: a 901 x 373 band of the photo (its top
         160.22% above), at 80% over the pale blue. The photo's own background
         is a shade lighter than the page's, so its edges fade out instead of
         showing as a box. */
      .hero__art {
        position: relative;
        width: 100%;
        max-width: 44rem;
        justify-self: center;
        aspect-ratio: 901 / 373;
        overflow: hidden;
        -webkit-mask-image:
          linear-gradient(to right, transparent, #000 10%, #000 90%, transparent),
          linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent);
        -webkit-mask-composite: source-in;
        mask-image:
          linear-gradient(to right, transparent, #000 10%, #000 90%, transparent),
          linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent);
        mask-composite: intersect;
      }
      .hero__art-img {
        position: absolute;
        left: 0;
        top: -160.22%;
        width: 100%;
        height: auto;
        opacity: 0.8;
      }

      /* Design CTA: brand pill with a soft shadow that deepens on hover.
         Overrides the shared button's outlined-invert hover for this one use;
         .k-btn is doubled so the override outranks the button's own
         color-variant rules regardless of stylesheet insertion order. */
      /* kirigami-600 (not the design's 500) so the 15px label meets the
         4.5:1 AA contrast minimum. */
      :host ::ng-deep .k-btn.k-btn.hero-cta {
        height: 3.625rem;
        padding: 0 2.5rem;
        border: 0;
        border-radius: 999px;
        background: var(--kirigami-600);
        color: var(--neu-0);
        box-shadow: 0 6px 18px rgba(45, 85, 121, 0.18);
        transition:
          background-color 0.18s ease,
          box-shadow 0.18s ease;
      }
      :host ::ng-deep .k-btn.k-btn.hero-cta:hover {
        background: var(--kirigami-700);
        color: var(--neu-0);
        box-shadow: 0 10px 24px rgba(45, 85, 121, 0.24);
      }
      /* The CTA's own drop shadow outranks the global focus ring, so the
         ring is restated here at the same specificity. */
      :host ::ng-deep .k-btn.k-btn.hero-cta:focus-visible {
        outline: none;
        box-shadow:
          inset 0 0 0 1px var(--neu-0),
          0 0 0 2px var(--neu-0),
          0 0 0 4px var(--kirigami-600);
      }
      :host ::ng-deep .k-btn.k-btn.hero-cta .k-label {
        font-size: 0.9375rem;
        font-weight: 700;
        letter-spacing: 0.06em;
      }

      /* The intro starts at opacity .01, not 0: Chrome excludes elements
         painted at opacity 0 from Largest Contentful Paint, which pushed the
         page's LCP onto whatever rendered later. Visually identical. */
      @keyframes hero-rise {
        from {
          opacity: 0.01;
          transform: translateY(22px);
          filter: blur(6px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
          filter: blur(0);
        }
      }
      @keyframes hero-leave {
        from {
          opacity: 1;
          transform: translateY(0);
        }
        to {
          opacity: 0;
          transform: translateY(-40px);
        }
      }

      /* Scroll-driven exit: the hero content drifts up and fades as the
         section leaves the viewport. Guarded so browsers without scroll
         timelines never run the keyframes on the document timeline. */
      @supports (animation-timeline: view()) {
        .hero__inner {
          animation-duration: 1s;
          animation-timing-function: linear;
          animation-fill-mode: both;
          animation-name: hero-leave;
          animation-timeline: view();
          animation-range: exit 10% exit 90%;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .hero__eyebrow,
        .hero__line,
        .hero__subtitle,
        .hero__actions,
        .hero__inner {
          animation: none;
        }
      }
    `,
  ],
})
export class HeroWidgetComponent {
  @Input() isUserLogged = false;
  @Output() readonly primary = new EventEmitter<void>();
  @Output() readonly secondary = new EventEmitter<void>();
}
